import { FC, useCallback, useEffect } from 'react'

import { FocusNode, useFocusNodeById, useSetFocus } from '@please/lrud'

import { popupStatus } from '@sportworld/js-sdk/src/constants/popup'
import { REQUEST_MATCHES_INTERVAL } from '@sportworld/js-sdk/src/constants/soccerData'
import { useMatchesNavigation } from '@sportworld/js-sdk/src/hooks/useMatchesNavigation'
import type { MatchItem } from '@sportworld/js-sdk/src/types/soccerDataTypes'
import { formatDateForMatchesRequest } from '@sportworld/js-sdk/src/utils/soccerData'

import { getMatches, getTournamentCalendar } from '@/actions'
import { createMatchPage } from '@/components/DataWidget/utils'
import { Loader } from '@/components/Loader'
import { orientation } from '@/constants/focus'
import { useCustomFocus, useDataWidgetTeamId } from '@/hooks'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { addThemeForPage } from '@/utils/utils'

import { Match } from './Match'
import { MatchesFallback } from './MatchesFallback'
import { WeekSwitcher } from './WeekSwitcher'
import {
  focusIds,
  MATCHES_SCROLL_OPTIONS,
  MAX_MATCHES_ON_PAGE,
} from '../constants'
import { MatchesScrollContainer, MatchesTable } from '../styles'
import { type DataWidgetPartProps } from '../types'

const WEEK_FOCUS_CHILD_INDEX = 0
const MATCH_FOCUS_CHILD_INDEX = 1

export const Matches: FC<DataWidgetPartProps> = ({
  contestantId,
  tournamentCalendarId,
}) => {
  const { t } = useCommonTranslation()
  const focusNode = useFocusNodeById(focusIds.matchesTable)
  const setFocus = useSetFocus()
  const {
    matches,
    loading,
    tournamentCalendar,
    setTournamentCalendar,
    setMatches,
  } = useStore((state) => state.soccerData)
  const {
    pagePopup,
    setPagePopupFocusId,
    setPagePopupStatus,
    setPagePopup,
    setPagePopupTournamentWeek,
  } = useStore((state) => state.popup)
  const { page } = useStore((state) => state.page)
  const teamId = useDataWidgetTeamId()
  const {
    defaultFocusChild: matchesContainerDefaultFocus,
    onMove: handleMatchesContainerMove,
    setDefaultFocusChild: setMatchesContainerDefaultFocus,
  } = useCustomFocus({
    focusId: focusIds.matchesContainer,
  })
  const { defaultFocusChild, onMove, onSelectWithScroll } = useCustomFocus({
    focusId: focusIds.matchesTable,
    scrollOptions: MATCHES_SCROLL_OPTIONS,
  })

  const {
    week,
    prevDisabled,
    nextDisabled,
    isFinished,
    isNotStarted,
    weekInRange,
    resetWeek,
    setWeekOnDate,
    onPrevWeek,
    onNextWeek,
  } = useMatchesNavigation(
    tournamentCalendar,
    pagePopup?.pageOptions?.tournamentWeek,
  )

  const isLoading = Object.values(loading).some((loading) => loading)
  const hasMaxVisibleMatches = matches.length > MAX_MATCHES_ON_PAGE

  useEffect(() => {
    if (
      tournamentCalendarId &&
      tournamentCalendar?.id !== tournamentCalendarId
    ) {
      void getTournamentCalendar(tournamentCalendarId)
    }
  }, [tournamentCalendarId, tournamentCalendar])

  useEffect(
    () => () => {
      setTournamentCalendar(null)
      setMatches([])
      resetWeek()
      setMatchesContainerDefaultFocus(WEEK_FOCUS_CHILD_INDEX)
    },
    [],
  )

  useEffect(() => {
    const focusId = pagePopup?.focusId
    if (matches.length && focusId) {
      setFocus(focusId)
      onSelectWithScroll(focusId, null)
      setPagePopupFocusId('')
    }
  }, [matches])

  useEffect(() => {
    if (!tournamentCalendarId) {
      return initializeMatchFetching()
    }

    if (!tournamentCalendar) {
      return
    }

    if (!weekInRange && isFinished) {
      handleSetLastCalendarWeek()
      return
    }
    if (!weekInRange && isNotStarted) {
      handleSetFirstCalendarWeek()
      return
    }

    if (tournamentCalendar?.id === tournamentCalendarId) {
      return initializeMatchFetching()
    }
  }, [week, tournamentCalendar, contestantId])

  const initializeMatchFetching = () => {
    const controller = new AbortController()

    void fetchMatches(controller.signal)
    const intervalId = setInterval(
      fetchMatches,
      REQUEST_MATCHES_INTERVAL,
      controller.signal,
    )

    return () => {
      clearInterval(intervalId)
      controller.abort()
    }
  }

  const fetchMatches = async (abortSignal?: AbortSignal) => {
    await getMatches(
      {
        contestantId: contestantId || teamId,
        tournamentCalendarId,
        matchTimeRangeStart: formatDateForMatchesRequest(week.start),
        matchTimeRangeEnd: formatDateForMatchesRequest(week.end),
      },
      abortSignal,
    )
  }

  const handleSetFirstCalendarWeek = useCallback(() => {
    tournamentCalendar && setWeekOnDate(tournamentCalendar.startDate || '')
  }, [tournamentCalendar])

  const handleSetLastCalendarWeek = useCallback(() => {
    tournamentCalendar && setWeekOnDate(tournamentCalendar.endDate || '')
  }, [tournamentCalendar])

  const handleClickMatch = (match: MatchItem) => {
    const title = `${match.home.name} ${t('soccerData.matches.vs')} ${
      match.away.name
    }`

    setMatchesContainerDefaultFocus(MATCH_FOCUS_CHILD_INDEX)
    setPagePopupTournamentWeek(week)
    const matchPage = createMatchPage(match, title, tournamentCalendarId)
    const pageWithTheme = addThemeForPage(matchPage, pagePopup || page)
    const focusId = focusNode
      ? focusNode.children[focusNode.focusedChildIndex || 0]
      : ''
    setPagePopup({ ...pageWithTheme, pageOptions: { focusId } })
    setPagePopupStatus(popupStatus.OPEN)
  }

  const renderMatches = () => {
    if (!matches.length && isLoading) {
      return <Loader />
    }

    if (!isLoading && !matches.length) {
      return <MatchesFallback translationKey="noData" />
    }

    return matches.map((match: any) => (
      <Match key={match.id} match={match} onClick={handleClickMatch} />
    ))
  }

  return (
    <FocusNode
      orientation="vertical"
      focusId={focusIds.matchesContainer}
      onMove={handleMatchesContainerMove}
      defaultFocusChild={matchesContainerDefaultFocus}
    >
      <WeekSwitcher
        week={week}
        nextDisabled={nextDisabled}
        prevDisabled={prevDisabled}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
      />
      <MatchesTable
        focusId={focusIds.matchesTable}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
        orientation={orientation.VERTICAL}
        disabled={!matches.length && !isFinished && !isNotStarted}
      >
        <MatchesScrollContainer
          id={focusIds.matchesTable}
          $fixedHeight={hasMaxVisibleMatches}
        >
          {renderMatches()}
        </MatchesScrollContainer>
      </MatchesTable>
    </FocusNode>
  )
}
